import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { FaHome, FaGraduationCap, FaFutbol, FaUsers, FaBriefcase, FaGlassCheers, FaEllipsisH } from 'react-icons/fa';
import Modal from '../layouts/Modal';
import Button from '../misc/Button';
import KnobToggle from '../misc/KnobToggle';
import httpService from '../../services/httpService';
import * as communityService from '../../services/communityService';
import { store } from '../../redux/store';
import { setSelectedCommunity } from '../../redux/slices/applicationSlice';
import ImageInput from '../forms/ImageInput';
import { CommunityType, CommunityFeatures, CommunityMemberPermissions, DashboardFeatures } from '../../types/misc';

interface CreateCommunityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export default function CreateCommunityModal({ open, setOpen, onSuccess }: CreateCommunityModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Final data
  const [communityType, setCommunityType] = useState<CommunityType | undefined>(undefined);
  const [features, setFeatures] = useState<CommunityFeatures>({} as CommunityFeatures);
  const [wantedDashboardFeatures, setWantedDashboardFeatures] = useState<DashboardFeatures>({} as DashboardFeatures);
  const [memberPermissions, setMemberPermissions] = useState<CommunityMemberPermissions>(
    {} as CommunityMemberPermissions,
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [picture, setPicture] = useState<string | undefined>(undefined);
  const [pictureAction, setPictureAction] = useState<'UPLOAD' | 'DELETE' | undefined>(undefined);

  const [connectedMembers, setConnectedMembers] = useState(true);
  const [enableDashboard, setEnableDashboard] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setStep(0);
      setCommunityType(undefined);
      setFeatures({} as CommunityFeatures);
      setMemberPermissions({} as CommunityMemberPermissions);
      setName('');
      setDescription('');
      setPicture(undefined);
      setPictureAction(undefined);
      setConnectedMembers(true);
      setEnableDashboard(true);
      setWantedDashboardFeatures({} as DashboardFeatures);
      setErrors({});
      setIsLoading(false);
    }
  }, [open]);

  const totalSteps = 4;

  // Pre-select features based on type (adapted from app logic)
  const getPreSelectedFeatures = (type?: CommunityType): CommunityFeatures => {
    if (!type) return {} as CommunityFeatures;
    switch (type) {
      case CommunityType.ROOMMATES:
      case CommunityType.STUDENTGROUP:
      case CommunityType.SPORTTEAM:
      case CommunityType.ASSOCIATION:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          STATS_AND_SCORES: true,
          TASKS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        } as CommunityFeatures;
      case CommunityType.COLLEAGUES:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          STATS_AND_SCORES: false,
          TASKS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: false,
        } as CommunityFeatures;
      case CommunityType.FRIENDS:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          STATS_AND_SCORES: false,
          TASKS: false,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        } as CommunityFeatures;
      case CommunityType.FAMILY:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          STATS_AND_SCORES: false,
          TASKS: false,
          COST_SPLITTING: false,
          PHOTO_ALBUMS: true,
        } as CommunityFeatures;
      case CommunityType.OTHER_COMMUNITY:
      default:
        return {
          EASY_SHARING: false,
          EVENTS: false,
          STATS_AND_SCORES: false,
          TASKS: false,
          COST_SPLITTING: false,
          PHOTO_ALBUMS: false,
        } as CommunityFeatures;
    }
  };

  const getDefaultMemberPermissions = (value = false): CommunityMemberPermissions => ({
    MEMBERS: { CREATE: value },
    EVENTS: { CREATE: value, UPDATE: value, DELETE: value },
    STATS_AND_SCORES: { CREATE: value, UPDATE: value, DELETE: value },
    TASKS: { CREATE: value, UPDATE: value, DELETE: value },
    COST_SPLITTING: { CREATE: value, UPDATE: value, DELETE: value },
    PHOTO_ALBUMS: { CREATE: value, UPDATE: value, DELETE: value },
  });

  const toggleMemberPreference = (category: keyof CommunityMemberPermissions) => {
    const prev = memberPermissions[category] ?? getDefaultMemberPermissions()[category]!;
    const prevHasTrue = Object.values(prev).some((v) => v === true);
    const newVal = !prevHasTrue;
    setMemberPermissions((old) => ({
      ...old,
      [category]: Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: newVal }), {} as any),
    }));
  };

  const handleCreate = async () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('page.dashboard.communities.errors.nameRequired');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const body: any = {
        name: name.trim(),
        description: description.trim(),
        connectedMembers,
        enableDashboard,
      };

      if (communityType) body.type = communityType;
      if (Object.keys(features || {}).length > 0) body.features = features;
      if (Object.keys(memberPermissions || {}).length > 0) body.memberPermissions = memberPermissions;
      if (Object.keys(wantedDashboardFeatures || {}).length > 0) body.dashboardFeatures = wantedDashboardFeatures;

      const { data: community } = await httpService.post<any>('/networks/communities', body, {
        params: { source: 'personal-dashboard' },
      });

      // If user uploaded an image locally, upload it to the server
      if (pictureAction === 'UPLOAD' && picture && community?.id) {
        try {
          const file = await fetch(picture).then((r) => r.blob() as Promise<File>);
          const form = new FormData();
          form.append('avatar', file);
          await httpService.post(`/networks/communities/${community.id}/profile-picture`, form);
        } catch (e) {
          // non-fatal; show a toast
          toast.warn(t('page.dashboard.communities.createModal.pictureUploadFailed') || 'Failed to upload picture');
        }
      }

      // Refresh local list and set selected
      await communityService.fetchCommunities();
      if (community?.id) {
        store.dispatch(setSelectedCommunity(community.id));
      }

      toast.success(t('page.dashboard.communities.createModal.success') || 'Community created');
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(t('page.dashboard.communities.createModal.error') || 'Failed to create community');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={<>{t('page.dashboard.communities.createModal.title') || 'Create community'}</>}
      maxWidth="max-w-3xl">
      {/* Step Indicators */}
      <div className="mb-8 flex gap-2">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={classNames('h-2 flex-1 rounded-full transition-all', {
              'bg-primary': index <= step,
              'bg-secondary-200': index > step,
            })}
          />
        ))}
      </div>

      {/* Step content */}

      {/* Step 0 — Choose Type */}
      {step === 0 && (
        <div className="flex flex-col gap-4">
          <h4 className="text-xl font-semibold text-primary">{t('businessChooseTypeScreen.title')}</h4>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
            {[
              { key: CommunityType.FRIENDS, id: 'friends', icon: <FaGlassCheers className="h-6 w-6" /> },
              { key: CommunityType.STUDENTGROUP, id: 'studentgroup', icon: <FaGraduationCap className="h-6 w-6" /> },
              { key: CommunityType.SPORTTEAM, id: 'sportteam', icon: <FaFutbol className="h-6 w-6" /> },
              { key: CommunityType.ROOMMATES, id: 'roommates', icon: <FaHome className="h-6 w-6" /> },
              { key: CommunityType.FAMILY, id: 'family', icon: <FaUsers className="h-6 w-6" /> },
              { key: CommunityType.COLLEAGUES, id: 'colleagues', icon: <FaBriefcase className="h-6 w-6" /> },
              { key: CommunityType.ASSOCIATION, id: 'association', icon: <FaUsers className="h-6 w-6" /> },
              { key: CommunityType.OTHER_COMMUNITY, id: 'otherCommunity', icon: <FaEllipsisH className="h-6 w-6" /> },
            ].map(({ key, id, icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setCommunityType(key);
                  // preselect features but do NOT auto-advance
                  setFeatures(getPreSelectedFeatures(key));
                }}
                className={classNames(
                  'flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-lg border p-4 text-center transition-colors',
                  communityType === key
                    ? 'border-primary bg-primary/5'
                    : 'border-secondary-200 hover:border-primary/30',
                )}>
                <div
                  className={classNames(
                    'flex h-12 w-12 items-center justify-center rounded-lg p-3',
                    communityType === key ? 'bg-primary text-white' : 'bg-secondary-100 text-primary',
                  )}>
                  {icon}
                </div>

                <div className="font-medium text-primary">{t(`businessChooseTypeScreen.types.${id}.name`)}</div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setStep(0)}>
              {t('general.back')}
            </Button>
            <Button variant="primary" onClick={() => setStep(1)} disabled={!communityType}>
              {t('general.next')}
            </Button>
          </div>
        </div>
      )}

      {/* Step 1 — Features */}
      {step === 1 && (
        <div className="flex flex-col gap-4">
          <h4 className="text-xl font-semibold text-primary">{t('BusinessFeaturesScreen.title')}</h4>

          <div className="flex flex-col gap-3">
            {[
              {
                key: 'EVENTS',
                label: t('BusinessFeaturesScreen.features.events.title'),
                description: t('BusinessFeaturesScreen.features.events.description'),
              },
              {
                key: 'EASY_SHARING',
                label: t('BusinessFeaturesScreen.features.easySharing.title'),
                description: t('BusinessFeaturesScreen.features.easySharing.description'),
              },
              {
                key: 'COST_SPLITTING',
                label: t('BusinessFeaturesScreen.features.costSplitting.title'),
                description: t('BusinessFeaturesScreen.features.costSplitting.description'),
              },
              {
                key: 'PHOTO_ALBUMS',
                label: t('BusinessFeaturesScreen.features.photoAlbums.title'),
                description: t('BusinessFeaturesScreen.features.photoAlbums.description'),
              },
              {
                key: 'TASKS',
                label: t('BusinessFeaturesScreen.features.tasks.title'),
                description: t('BusinessFeaturesScreen.features.tasks.description'),
              },
              {
                key: 'STATS_AND_SCORES',
                label: t('BusinessFeaturesScreen.features.statsAndScores.title'),
                description: t('BusinessFeaturesScreen.features.statsAndScores.description'),
              },
            ].map(({ key, label, description: featureDescription }) => {
              const selected = !!(features as any)[key];
              return (
                <div key={key} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex flex-col">
                    <div className="font-medium text-primary">{label}</div>
                    {featureDescription && <div className="mt-1 text-sm text-details/70">{featureDescription}</div>}
                  </div>

                  <div>
                    <KnobToggle
                      value={selected}
                      onChange={(v) => setFeatures({ ...features, [key as keyof CommunityFeatures]: v })}
                    />
                  </div>
                </div>
              );
            })}

            <h1 className="mt-2 text-xl font-semibold text-primary">
              {t('BusinessFeaturesScreen.dashboardFeatures.title')}
            </h1>
            <p className="-mt-4 text-details/70">{t('BusinessFeaturesScreen.dashboardFeatures.description')}</p>

            {[
              {
                key: 'MEMBER_ADMINISTRATION',
                label: t('BusinessFeaturesScreen.dashboardFeatures.memberAdministration.title'),
                description: t('BusinessFeaturesScreen.dashboardFeatures.memberAdministration.description'),
              },
              {
                key: 'PARTICIPATION_INSIGHTS',
                label: t('BusinessFeaturesScreen.dashboardFeatures.participationInsights.title'),
                description: t('BusinessFeaturesScreen.dashboardFeatures.participationInsights.description'),
              },
              {
                key: 'COMMUNICATION',
                label: t('BusinessFeaturesScreen.dashboardFeatures.communication.title'),
                description: t('BusinessFeaturesScreen.dashboardFeatures.communication.description'),
              },
              {
                key: 'FINANCIAL_ADMINISTRATION',
                label: t('BusinessFeaturesScreen.dashboardFeatures.financialAdministration.title'),
                description: t('BusinessFeaturesScreen.dashboardFeatures.financialAdministration.description'),
              },
            ].map(({ key, label, description: featureDescription }) => {
              const selected = !!(wantedDashboardFeatures as any)[key];
              return (
                <div key={key} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex flex-col">
                    <div className="font-medium text-primary">{label}</div>
                    {featureDescription && <div className="mt-1 text-sm text-details/70">{featureDescription}</div>}
                  </div>

                  <div>
                    <KnobToggle
                      value={selected}
                      onChange={(v) =>
                        setWantedDashboardFeatures({ ...wantedDashboardFeatures, [key as keyof DashboardFeatures]: v })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setStep(0)}>
              {t('general.back')}
            </Button>
            <Button variant="primary" onClick={() => setStep(2)}>
              {t('general.continue')}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 — Member preferences */}
      {step === 2 && (
        <div className="flex flex-col gap-4">
          <h4 className="text-xl font-semibold text-primary">{t('businessChooseSettingsScreen.title')}</h4>

          <div className="flex flex-col gap-2">
            {Object.entries(getDefaultMemberPermissions()).map(([category, operations]) => {
              const isSelected = Object.values((memberPermissions as any)[category] || operations).some(Boolean);

              return (
                <div key={category} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex flex-col">
                    <div className="font-medium text-primary">
                      {t(`businessChooseSettingsScreen.preferences.${category}.title`)}
                    </div>
                    {t(`businessChooseSettingsScreen.preferences.${category}.description`) && (
                      <div className="mt-1 text-sm text-details/70">
                        {t(`businessChooseSettingsScreen.preferences.${category}.description`)}
                      </div>
                    )}
                  </div>

                  <div>
                    <KnobToggle value={isSelected} onChange={() => toggleMemberPreference(category as any)} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setStep(1)}>
              {t('general.back')}
            </Button>
            <Button variant="primary" onClick={() => setStep(3)}>
              {t('general.continue')}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — Name, Image, Description (final) */}
      {step === 3 && (
        <div className="flex flex-col gap-4">
          <h4 className="text-xl font-semibold text-primary">{t('businessChooseNameScreen.title')}</h4>

          {/* Picture on the left and Name on the right on md+ */}
          <div className="flex flex-col items-start gap-4 md:flex-row">
            <div className="w-full flex-shrink-0 md:w-48">
              <label className="mb-1 block text-sm font-medium text-primary">
                {t('communityCreateScreen.picture')}
              </label>
              <div className="h-48 w-full md:h-48">
                <ImageInput
                  className="h-full w-full rounded-lg object-cover"
                  iconClassName="text-primary"
                  image={picture}
                  setImage={(img) => setPicture(img)}
                  setImageAction={(a) => setPictureAction(a)}
                />
              </div>
            </div>

            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-primary">{t('communityCreateScreen.name')} *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                placeholder={t('communityCreateScreen.name')}
                className={classNames(
                  'w-full rounded-lg border-none !bg-secondary-50 p-2 placeholder:text-primary',
                  errors.name && 'ring-2 ring-red-500',
                )}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}

              <div className="mt-3">
                <label className="mb-1 block text-sm font-medium text-primary">
                  {t('communityCreateScreen.description')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-lg border-none p-2"
                  placeholder={t('communityCreateScreen.description')}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setStep(2)}>
              {t('general.back')}
            </Button>
            <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
              {t('page.dashboard.communities.createModal.create')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
